// Avatars and logos are stored as small files: longest edge 512 px, about 200 KB.
export const MAX_IMAGE_EDGE = 512;
export const MAX_IMAGE_BYTES = 200_000;
// Larger source files are refused before they reach the cropper.
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const QUALITIES = [0.85, 0.7, 0.55, 0.4];

const toBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Image encoding failed")),
      type,
      quality,
    ),
  );

// Lowers the quality until the image fits MAX_IMAGE_BYTES. Browsers that cannot
// write the type (Safari has no WebP) silently return PNG, so the caller checks blob.type.
const encode = async (canvas: HTMLCanvasElement, type: string) => {
  let blob = await toBlob(canvas, type, QUALITIES[0]);
  for (const quality of QUALITIES.slice(1)) {
    if (blob.type !== type || blob.size <= MAX_IMAGE_BYTES) break;
    blob = await toBlob(canvas, type, quality);
  }
  return blob;
};

const hasTransparency = (context: CanvasRenderingContext2D) => {
  const { data } = context.getImageData(
    0,
    0,
    context.canvas.width,
    context.canvas.height,
  );
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
};

const EXTENSIONS: Record<string, string> = {
  "image/webp": "webp",
  "image/jpeg": "jpg",
  "image/png": "png",
};

// Scales the image down to MAX_IMAGE_EDGE and encodes it as WebP; without WebP
// support as JPEG, or as PNG when the image has transparent pixels.
export async function shrinkImage(
  source: CanvasImageSource & { width: number; height: number },
  name = "image",
): Promise<File> {
  const scale = Math.min(
    1,
    MAX_IMAGE_EDGE / Math.max(source.width, source.height),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(source.width * scale));
  canvas.height = Math.max(1, Math.round(source.height * scale));
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("Canvas not available");
  context.imageSmoothingQuality = "high";
  context.drawImage(source, 0, 0, canvas.width, canvas.height);

  let blob = await encode(canvas, "image/webp");
  if (blob.type !== "image/webp") {
    blob = hasTransparency(context)
      ? await toBlob(canvas, "image/png")
      : await encode(canvas, "image/jpeg");
  }
  const baseName = name.replace(/\.[^.]*$/, "") || "image";
  const extension = EXTENSIONS[blob.type] ?? "png";
  return new File([blob], `${baseName}.${extension}`, { type: blob.type });
}

export const isInlineImage = (src: unknown): src is string =>
  typeof src === "string" &&
  (src.startsWith("data:") || src.startsWith("blob:"));
